import React from "react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { LiveResultCard } from "../components/LiveResultCard";
import { InternalLinks } from "../components/InternalLinks";
import { FAQAccordion, DEFAULT_FAQS } from "../components/FAQAccordion";
import { SEOContentSection } from "../components/SEOContentSection";
import { useSEO } from "../hooks/useSEO";
import { SITE_URL } from "../lib/api";
import { relatedFor } from "../data/pages";

function crumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ name: "Home", to: "/" }, ...items].map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.label || it.name,
      item: SITE_URL + (it.to || ""),
    })),
  };
}

export default function ResultPage({ config }) {
  const { result, previous, common } = relatedFor(config.variant);

  useSEO({
    title: config.title,
    description: config.description,
    path: config.slug,
    jsonLd: [
      crumbJsonLd([{ label: config.kicker }, { label: config.h1, to: config.slug }]),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: DEFAULT_FAQS.slice(0, 4).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const content = [
    {
      h: `About ${config.h1.replace(" Live Result", "").replace(" Today", "")}`,
      p: config.intro,
    },
    {
      h: "Result timings",
      p: "The First Round (F/R) is generally declared around 10:30 AM and the Second Round (S/R) around 11:30 AM (IST). The result card above updates automatically as soon as new numbers are available, with no manual entry and no full-page reload.",
    },
    {
      h: "How to read the result card",
      p: "The large circled numbers show the winning F/R and SR values for the current date. If the external result source is briefly unavailable, the last valid result stays visible so the page never breaks.",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[{ label: config.kicker }, { label: config.h1 }]} />
      <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-900 mb-2">{config.h1}</h1>
      <p className="text-slate-600 max-w-3xl mb-8 text-[15px] leading-relaxed">{config.intro}</p>

      <LiveResultCard
        title={`${config.h1
          .toUpperCase()
          .replace(" LIVE RESULT", "")
          .replace(" TODAY", "")
          .replace(/ RESULT$/, "")
          .trim()} RESULT`}
        previousHref={previous.slug}
      />

      <div className="mt-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-10">
          <SEOContentSection heading={`${config.h1} — Details`} blocks={content} />
          <FAQAccordion />
        </div>
        <div className="lg:col-span-1 space-y-6">
          <InternalLinks
            title="Related pages"
            links={[
              { label: `${previous.h1}`, to: previous.slug },
              { label: `${common.h1}`, to: common.slug },
              { label: "Latest Updates & News", to: "/blog/" },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
